import '../../i18n/autoTranslate';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import SpotlightCard from '../../../components/ui/SpotlightCard';

/**
 * Map raw level → human rank label. Used as a fallback when the backend
 * doesn't supply `rank` (was previously dead code in the original file).
 */
const getRankTitle = (level) => {
  const lv = Number(level) || 1;
  if (lv < 3) return 'Beginner';
  if (lv < 6) return 'Intermediate';
  if (lv < 9) return 'Advanced';
  if (lv < 12) return 'Professional';
  return 'Expert';
};

const PLAN_STYLES = {
  Free: 'border-white/15 bg-white/5 text-white/70',
  Pro: 'border-purple-300/30 bg-purple-500/15 text-purple-100',
  Premium: 'border-amber-300/30 bg-amber-500/15 text-amber-100',
  Enterprise: 'border-emerald-300/30 bg-emerald-500/15 text-emerald-100',
};

const WelcomeBanner = ({
  userName = 'Alex',
  currentStreak = 0,
  level = 1,
  xp = 0,
  next_level_xp = 1000,
  id = 'welcome-banner',
  rank = '',
  plan = '',
}) => {
  const navigate = useNavigate();

  const currentHour = new Date().getHours();
  const getGreeting = () => {
    if (currentHour < 12) return 'Good morning';
    if (currentHour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Numeric safety
  const safeLevel = Math.max(1, Number(level) || 1);
  const safeNextLevelXp =
    Number.isFinite(Number(next_level_xp)) && Number(next_level_xp) > 0 ? Number(next_level_xp) : 0;
  const safeXp = Number.isFinite(Number(xp)) && Number(xp) > 0 ? Number(xp) : 0;
  const safeStreak = Math.max(0, Number(currentStreak) || 0);

  // Show real progress only when the backend gave us a target.
  const hasXpTarget = safeNextLevelXp > 0;
  const progressPct = hasXpTarget
    ? Math.min(100, Math.max(0, Math.round((safeXp / safeNextLevelXp) * 100)))
    : 0;
  const xpToGo = hasXpTarget ? Math.max(0, safeNextLevelXp - safeXp) : 0;

  // Rank fallback so the level tile never shows an empty subtitle.
  const displayRank = rank && rank !== 'Unranked' ? rank : getRankTitle(safeLevel);

  // Plan badge — only show if the backend actually returned one.
  const planBadgeClass = PLAN_STYLES[plan] || PLAN_STYLES.Free;

  // Make the stat tiles feel actionable. The tiles already lead somewhere
  // logical (streak → start a session, level → analytics), so wire that up
  // and add proper keyboard support.
  const goStartSession = () => navigate('/interview-setup-configuration');
  const goAnalytics = () => navigate('/progress-analytics-insights');

  const onTileKey = (handler) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };

  return (
    <SpotlightCard
      id={id}
      className="relative rounded-3xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_18px_60px_rgba(0,0,0,0.40)] p-5 sm:p-7 custom-spotlight-card"
      spotlightColor="rgba(139, 92, 246, 0.18)"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(900px_520px_at_20%_20%,rgba(139,92,246,0.22),transparent_58%)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/25 to-black/35" />
      </div>

      {/*
        FIX: Mobile layout reworked. Old layout used flex-row which squeezed
        two stat tiles side-by-side on narrow screens. Now: greeting on top,
        stats are a 2-col grid below on mobile, side-by-side from md+.
      */}
      <div className="relative z-10 flex flex-col md:flex-row md:items-stretch gap-6">
        {/* LEFT: greeting + XP */}
        <div className="flex-[3] min-w-0">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white truncate">
                {getGreeting()}, <span className="text-purple-200">{userName}</span>!
              </h1>
              {plan && (
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${planBadgeClass}`}
                  title={`${plan} plan`}
                >
                  {plan}
                </span>
              )}
            </div>
            <p className="text-gray-300 text-sm md:text-base">
              Ready to ace your next interview? Let&apos;s {safeLevel < 3 ? 'start' : 'continue'} your
              journey to success.
            </p>
          </div>

          {/* Progress to Next Level */}
          <div className="mt-6 space-y-2">
            <div className="flex items-center text-sm gap-3">
              <div className="w-11 h-11 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center shrink-0">
                <Icon name="Zap" size={22} className="text-indigo-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-gray-300 text-xs sm:text-sm">
                    Progress to Level {safeLevel + 1}
                  </div>
                  {hasXpTarget && (
                    <div className="text-white/90 text-xs sm:text-sm font-medium tabular-nums">
                      {safeXp.toLocaleString()} / {safeNextLevelXp.toLocaleString()} XP
                    </div>
                  )}
                </div>
                {hasXpTarget ? (
                  <div className="text-[11px] text-white/60 mt-0.5">
                    {xpToGo > 0
                      ? `${xpToGo.toLocaleString()} XP to go`
                      : 'Level complete — ready to advance!'}
                  </div>
                ) : (
                  <div className="text-[11px] text-white/60 mt-0.5">
                    Complete sessions to start earning XP.
                  </div>
                )}
              </div>
            </div>

            <div
              className="w-full bg-white/10 rounded-full h-2 backdrop-blur-sm overflow-hidden"
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Progress to level ${safeLevel + 1}`}
            >
              <div
                className="bg-gradient-to-r from-indigo-500 to-purple-300 h-2 rounded-full shadow-[0px_0px_5px_rgba(240,166,253,0.9)] transition-all duration-500 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* RIGHT: stats — grid on mobile, column on md+ */}
        <div className="flex-1 min-w-0 grid grid-cols-2 gap-3 md:flex md:flex-col md:gap-4 md:justify-center md:min-w-[200px]">
          {/* Streak tile */}
          <div
            role="button"
            tabIndex={0}
            onClick={goStartSession}
            onKeyDown={onTileKey(goStartSession)}
            aria-label={`Day streak: ${safeStreak}. Press to start a new session.`}
            className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 transition hover:bg-white/10 hover:border-white/20 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60"
          >
            <div className="h-11 w-11 rounded-xl border border-white/10 bg-white/10 flex items-center justify-center shrink-0">
              <Icon name="Flame" size={22} className="text-purple-300" />
            </div>
            <div className="min-w-0">
              <div className="text-lg font-bold text-white leading-none tabular-nums">
                {safeStreak}
              </div>
              <div className="text-xs text-white/80">Day Streak</div>
              {safeStreak === 0 && (
                <div className="text-[10px] text-purple-200/80 mt-0.5">Let&apos;s get started!</div>
              )}
            </div>
          </div>

          {/* Level tile */}
          <div
            role="button"
            tabIndex={0}
            onClick={goAnalytics}
            onKeyDown={onTileKey(goAnalytics)}
            aria-label={`Level ${safeLevel}, ${displayRank}. Press to view analytics.`}
            className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 transition hover:bg-white/10 hover:border-white/20 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60"
          >
            <div className="h-11 w-11 rounded-xl border border-white/10 bg-white/10 flex items-center justify-center shrink-0">
              <Icon name="Trophy" size={22} className="text-indigo-300" />
            </div>
            <div className="min-w-0">
              <div className="text-lg font-bold text-white leading-none">Level {safeLevel}</div>
              <div className="text-xs text-gray-300 truncate">{displayRank}</div>
            </div>
          </div>
        </div>
      </div>
    </SpotlightCard>
  );
};

export default WelcomeBanner;
